// Sourcing Request-related filters

import {
	SourcingLineStatus,
	SourcingLineStatusLabels,
	SourcingRequestStatus,
	SourcingRequestType,
} from '@supply-mover-2-0/shared';

import {
	createDateRangeFilter,
	createEntityLookupFilter,
	createKeywordFilter,
	createStatusFilter,
} from './factories';
import type { FilterRegistry } from './types';

export const sourcingRequestFilters: FilterRegistry = {
	sourcingRequestKeywords: {
		...createKeywordFilter({
			label: 'Search',
			placeholder: 'Search requests...',
			objectTable: 'sourcing_request',
			listKeys: ['sourcing_request'],
		}),
		queryParamName: 'sourcingRequestKeywords',
		queryMapping: {
			objectTable: 'sourcing_request',
			fieldName: 'name',
			joinType: 'direct',
		},
	},

	sourcingRequestStatus: {
		type: 'multi-select',
		label: 'Status',
		placeholder: 'Select status...',
		colorScheme: 'blue',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'sourcingRequestStatus',
		multiple: true,
		valueExtractor: {
			property: 'id',
		},
		options: Object.values(SourcingRequestStatus).map((status) => ({
			id: status,
			label:
				status === SourcingRequestStatus.IN_PROGRESS
					? 'In Progress'
					: status === SourcingRequestStatus.PARTIALLY_FULFILLED
						? 'Partially Fulfilled'
						: (status as string).charAt(0).toUpperCase() +
							(status as string).slice(1),
		})),
		queryMapping: {
			objectTable: 'sourcing_request',
			fieldName: 'status',
			joinType: 'direct',
		},
		format: 'array',
		listKeys: ['sourcing_request'],
	},

	sourcingRequestType: {
		type: 'multi-select',
		label: 'Type',
		placeholder: 'Select type...',
		colorScheme: 'purple',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'sourcingRequestType',
		multiple: true,
		valueExtractor: {
			property: 'id',
		},
		options: Object.values(SourcingRequestType).map((type) => ({
			id: type,
			label: type.charAt(0).toUpperCase() + type.slice(1),
		})),
		queryMapping: {
			objectTable: 'sourcing_request',
			fieldName: 'type',
			joinType: 'direct',
		},
		format: 'array',
		listKeys: ['sourcing_request'],
	},

	sourcingRequestPriority: {
		...createStatusFilter({
			label: 'Priority',
			options: [
				{ id: 'low', label: 'Low' },
				{ id: 'normal', label: 'Normal' },
				{ id: 'high', label: 'High' },
				{ id: 'urgent', label: 'Urgent' },
			],
			objectTable: 'sourcing_request',
			fieldName: 'priority',
			listKeys: ['sourcing_request'],
			colorScheme: 'orange',
		}),
		queryParamName: 'sourcingRequestPriority',
	},

	sourcingRequestClient: {
		...createEntityLookupFilter({
			label: 'Client',
			placeholder: 'Search clients...',
			apiEndpoint: '/clients',
			objectTable: 'sourcing_request',
			fieldName: '_fk_client__id',
			listKeys: ['sourcing_request'],
			displayFields: {
				id: 'id',
				primary: 'name',
				secondary: 'externalId',
			},
			colorScheme: 'green',
			searchable: true,
		}),
		searchFilterName: 'clientSearch',
		apiParams: {
			page: '1',
			limit: '10',
			fields: JSON.stringify(['client.id', 'client.name', 'client.externalId']),
		},
		queryParamName: 'sourcingRequestClient',
		loadOnFocus: false,
		valueExtractor: {
			property: 'id',
			transform: 'number',
		},
	},

	sourcingRequestAssignedUser: {
		...createEntityLookupFilter({
			label: 'Assigned To',
			placeholder: 'Search users...',
			apiEndpoint: '/users',
			objectTable: 'sourcing_request',
			fieldName: '_fk_user__id',
			listKeys: ['sourcing_request'],
			displayFields: {
				id: 'id',
				primary: 'name',
			},
			colorScheme: 'blue',
			searchable: true,
		}),
		options: [{ id: 'ME', label: 'Me' }],
		queryParamName: 'sourcingRequestAssignedUser',
		loadOnFocus: false,
		valueExtractor: {
			property: 'id',
			transform: 'number',
		},
	},

	sourcingRequestCreatedDateRange: {
		...createDateRangeFilter({
			label: 'Created Date',
			objectTable: 'sourcing_request',
			dateField: '_created_date',
			listKeys: ['sourcing_request'],
		}),
		queryParamName: 'sourcingRequestCreatedDateRange',
		colorScheme: 'gray',
	},

	/**
	 * Filter on `sourcing_request.target_date`. Used by the Coming Due
	 * and Overdue list tabs (default-value-driven), and available in
	 * the filter sidebar so buyers can build custom date-window views
	 * (e.g. "show me what's due this month").
	 */
	sourcingRequestTargetDateRange: {
		...createDateRangeFilter({
			label: 'Target Date',
			objectTable: 'sourcing_request',
			dateField: 'target_date',
			listKeys: ['sourcing_request'],
		}),
		queryParamName: 'sourcingRequestTargetDateRange',
		colorScheme: 'orange',
	},

	sourcingRequestLineStatus: {
		type: 'multi-select',
		label: 'Line Status',
		placeholder: 'Select line status...',
		colorScheme: 'blue',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'sourcingRequestLineStatus',
		multiple: true,
		valueExtractor: {
			property: 'id',
		},
		options: Object.values(SourcingLineStatus).map((status) => ({
			id: status,
			label: SourcingLineStatusLabels[status],
		})),
		queryMapping: {
			objectTable: 'sourcing_request',
			joinType: 'exists',
			joinTable: 'sourcing_request_line',
			joinConditions: {
				sourceField: '_id',
				targetField: '_fk_sourcing_request__id',
				filterField: 'line_status',
			},
		},
		format: 'array',
		listKeys: ['sourcing_request'],
	},

	/**
	 * Filter SRs to those with at least one line assigned to a given vendor.
	 * Powers the "Sourcing Requests" tab on Vendor Detail. Uses an EXISTS
	 * subquery on `sourcing_request_line` (vendors attach to lines, not the
	 * SR itself) so the SR row count stays correct regardless of how many
	 * lines per SR match the vendor.
	 */
	sourcingRequestLineVendor: {
		type: 'multi-select',
		label: 'Line Vendor',
		placeholder: 'Search vendors...',
		colorScheme: 'orange',
		searchable: true,
		loadOnFocus: false,
		apiEndpoint: '/vendors',
		searchFilterName: 'vendorSearch',
		apiParams: {
			page: '1',
			limit: '10',
			fields: JSON.stringify(['vendor.id', 'vendor.name', 'vendor.externalId']),
		},
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'name' as keyof unknown,
			secondary: 'externalId' as keyof unknown,
		},
		queryParamName: 'sourcingRequestLineVendor',
		multiple: true,
		valueExtractor: {
			property: 'id',
			transform: 'number',
		},
		queryMapping: {
			objectTable: 'sourcing_request',
			joinType: 'exists',
			joinTable: 'sourcing_request_line',
			joinConditions: {
				sourceField: '_id',
				targetField: '_fk_sourcing_request__id',
				filterField: '_fk_vendor__id',
			},
		},
		format: 'array',
		listKeys: ['sourcing_request'],
	},

	/**
	 * Filter SRs to those with at least one line referencing a given catalog
	 * item. Powers the "Sourcing Requests" tab on Item Detail. Same EXISTS
	 * pattern as `sourcingRequestLineVendor`.
	 */
	sourcingRequestLineItem: {
		type: 'multi-select',
		label: 'Line Item',
		placeholder: 'Search items...',
		colorScheme: 'purple',
		searchable: true,
		loadOnFocus: false,
		apiEndpoint: '/items',
		searchFilterName: 'itemSearch',
		apiParams: {
			page: '1',
			limit: '10',
			fields: JSON.stringify([
				'item.id',
				'item.description',
				'item.externalId',
			]),
		},
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'description' as keyof unknown,
			secondary: 'externalId' as keyof unknown,
		},
		queryParamName: 'sourcingRequestLineItem',
		multiple: true,
		valueExtractor: {
			property: 'id',
			transform: 'number',
		},
		queryMapping: {
			objectTable: 'sourcing_request',
			joinType: 'exists',
			joinTable: 'sourcing_request_line',
			joinConditions: {
				sourceField: '_id',
				targetField: '_fk_sourcing_request__id',
				filterField: '_fk_item__id',
			},
		},
		format: 'array',
		listKeys: ['sourcing_request'],
	},
};
